import {
    Badge,
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@repo/shadcn';
import { ColumnDef, Row } from '@tanstack/react-table';
import { ChevronDownIcon, Edit, Loader2Icon, Trash } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ButtonGroup } from '@components/ui/button';

import { Route } from '@/constants';
import { TUser } from '@/features/slice/users/users/user.schema';
import { useUserList } from '@/modules/users/user-list/user-list.hook';

const UserListActionCell = ({ row }: { row: Row<TUser> }) => {
    const {
        handleActivateUser,
        handleDeleteUser,
        handleSuspendUser,
        isDeleting,
        handleEditUser,
    } = useUserList();

    const renderSuspendActivate = () => {
        if (row.original.status === 'active') {
            return (
                <DropdownMenuItem
                    className={'text-warning flex gap-2'}
                    onClick={() => handleSuspendUser(row.original.id)}
                >
                    <Trash className={'size-4'} />
                    Suspend
                </DropdownMenuItem>
            );
        }
        // Placeholder for suspend/activate logic if needed in the future
        return (
            <DropdownMenuItem
                className={'text-warning flex gap-2'}
                onClick={() => handleActivateUser(row.original.id)}
            >
                <Trash className={'size-4'} />
                Activate
            </DropdownMenuItem>
        );
    };

    return (
        <div className={'flex justify-end'}>
            <ButtonGroup>
                {/*<Link to={`${Route.USERS}/${row.original.id}`}>*/}
                <Button
                    type={'button'}
                    onClick={() => handleEditUser(row.original.id)}
                    variant={'outline'}
                >
                    <Edit className={'size-4'} />
                    Edit
                </Button>
                {/*</Link>*/}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="!pl-2">
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="[--radius:1rem]"
                    >
                        <DropdownMenuGroup>
                            {/*<DropdownMenuItem*/}
                            {/*    className={'flex gap-2'}*/}
                            {/*    onClick={() => handleEditUser(row.original.id)}*/}
                            {/*>*/}
                            {/*    <Edit className={'size-4'} />*/}
                            {/*    Edit*/}
                            {/*</DropdownMenuItem>*/}

                            {renderSuspendActivate()}

                            <DropdownMenuItem
                                className={'flex gap-2 text-destructive'}
                                onClick={() =>
                                    handleDeleteUser(row.original.id)
                                }
                            >
                                {isDeleting ? (
                                    <Loader2Icon className={'animate-spin'} />
                                ) : (
                                    <>
                                        <Trash className={'size-4'} />
                                        Delete
                                    </>
                                )}
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </ButtonGroup>
        </div>
    );
};

export const userListColumns: ColumnDef<TUser>[] = [
    {
        accessorKey: 'name',
        header: () => <div>Name</div>,
        cell: ({ row }) => {
            return (
                <div>
                    {/* <Link
                        to={`${Route.USERS}/${row.original.id}`}
                        className={'duration-300 hover:underline'}
                    > */}
                    {row.original.name}
                    {/* </Link> */}
                </div>
            );
        },
    },

    {
        accessorKey: 'email',
        header: () => <div>Email</div>,
        cell: ({ row }) => {
            return <div>{row.original.email}</div>;
        },
    },

    {
        accessorKey: 'phone_number',
        header: () => <div>Phone Number</div>,
        cell: ({ row }) => {
            return <div>{row.original.phone_number}</div>;
        },
    },

    {
        accessorKey: 'role',
        header: () => <div>Role</div>,
        cell: ({ row }) => {
            return (
                <div>
                    <Link
                        to={`${Route.ROLES}/${row.original.role_id}`}
                        className={'duration-300 hover:underline'}
                    >
                        {row.original.role.name}
                    </Link>
                </div>
            );
        },
    },

    {
        accessorKey: 'status',
        header: () => <div>Status</div>,
        cell: ({ row }) => {
            return (
                <div>
                    <UserStatusBadge
                        status={row.original.status || 'Unknown'}
                    />
                </div>
            );
        },
    },

    {
        accessorKey: 'actions',
        header: () => <div className={'text-right'}>Actions</div>,
        cell: UserListActionCell,
    },
];

const UserStatusBadge = ({ status }: { status: string }) => {
    let colorClass = 'bg-gray-500 text-white';

    switch (status.toLowerCase()) {
        case 'active':
            colorClass = 'bg-green-500 text-white';
            break;
        case 'inactive':
            colorClass = 'bg-red-500 text-white';
            break;
        case 'pending':
            colorClass = 'bg-yellow-500 text-black';
            break;
    }

    const statusText =
        status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

    return <Badge className={colorClass}>{statusText}</Badge>;
};
